import { findMatchingAllergenExclusion } from '../label/allergen-exclusions';
import {
  type AllergenCategoryCode,
  EU_ALLERGEN_CATEGORY_CODES,
} from '../label/allergen-substances';
import { findAllergenEmphasisRanges } from './allergen-emphasis';

/**
 * Chip "Forse: latte" accanto a una voce creata da incolla/foto (DEV-375): la
 * sostanza del catalogo il cui nome (o sinonimo) compare nel nome della voce,
 * cercata con lo stesso finder che mette in grassetto la sostanza in etichetta.
 * E' solo un SUGGERIMENTO da accettare con un tocco: non si precompila e non si
 * salva mai da solo — un nome non prova la composizione.
 */

export interface AllergenSuggestionSubstance {
  id: string;
  name: string;
  synonyms: readonly string[];
}

/** Forma di `GET /allergens` (categorie con sostanze annidate). */
export interface AllergenSuggestionCatalogEntry {
  id: string;
  code: string;
  substances?: readonly AllergenSuggestionSubstance[];
}

export interface AllergenSubstanceSuggestion {
  allergenId: string;
  allergenCode: string;
  substanceId: string;
  substanceName: string;
  /** Testo della voce che ha fatto scattare il suggerimento. */
  matchedText: string;
}

/**
 * Omonimi vegetali: il sinonimo c'e' ma la sostanza no ("burro di cacao" non
 * e' burro, "latte di cocco" non e' latte). Scartarli evita un suggerimento
 * sbagliato; la voce resta comunque da completare a mano.
 */
const NON_ALLERGEN_HOMONYMS: ReadonlyArray<{ phrase: RegExp; categoryCode: AllergenCategoryCode }> =
  [
    {
      phrase: /burro\s+di\s+(?:cacao|karit[eé]|arachidi|mandorle|nocciole|anacardi|cocco)/iu,
      categoryCode: 'MILK',
    },
    {
      phrase: /latte\s+(?:di|d['’])\s*(?:cocco|mandorla|soia|riso|avena|nocciola)/iu,
      categoryCode: 'MILK',
    },
    { phrase: /(?:cocoa|shea|peanut|nut)\s+butter/iu, categoryCode: 'MILK' },
    { phrase: /(?:coconut|almond|soy|soya|rice|oat)\s+milk/iu, categoryCode: 'MILK' },
  ];

function isAllergenCategoryCode(code: string): code is AllergenCategoryCode {
  return (EU_ALLERGEN_CATEGORY_CODES as readonly string[]).includes(code);
}

function isHomonymMatch(componentName: string, categoryCode: string, matchStart: number): boolean {
  return NON_ALLERGEN_HOMONYMS.some((homonym) => {
    if (homonym.categoryCode !== categoryCode) {
      return false;
    }
    const occurrence = homonym.phrase.exec(componentName);
    return (
      occurrence !== null &&
      matchStart >= occurrence.index &&
      matchStart < occurrence.index + occurrence[0].length
    );
  });
}

function isExemptDerivative(componentName: string, categoryCode: string): boolean {
  return (
    isAllergenCategoryCode(categoryCode) &&
    findMatchingAllergenExclusion(componentName, categoryCode) !== undefined
  );
}

/** Sostanze del catalogo nominate nel nome della voce, in ordine di apparizione. */
export function suggestAllergenSubstances(
  componentName: string,
  catalog: readonly AllergenSuggestionCatalogEntry[],
): AllergenSubstanceSuggestion[] {
  if (componentName.trim() === '') {
    return [];
  }
  const suggestions: Array<AllergenSubstanceSuggestion & { start: number }> = [];
  for (const allergen of catalog) {
    if (isExemptDerivative(componentName, allergen.code)) {
      continue;
    }
    for (const substance of allergen.substances ?? []) {
      const [range] = findAllergenEmphasisRanges(componentName, [
        { names: [substance.name, ...substance.synonyms] },
      ]);
      if (!range || isHomonymMatch(componentName, allergen.code, range.start)) {
        continue;
      }
      suggestions.push({
        allergenId: allergen.id,
        allergenCode: allergen.code,
        substanceId: substance.id,
        substanceName: substance.name,
        matchedText: componentName.slice(range.start, range.end),
        start: range.start,
      });
    }
  }
  return suggestions
    .sort((left, right) => left.start - right.start)
    .map(({ start: _start, ...suggestion }) => suggestion);
}
